// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/recurring-donation.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / header-actions).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { recurringDonationsApi } from "../../api/recurring-donations";
import type { RecurringDonation } from "../../api/types";
// override-block:imports BEGIN
import type { RecurringDonationStatus, RecurringFrequency } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1330 — bulk-tag UX (closes DRIFT-648).
import { openBulkTagDialog } from "../../lib/bulk-tag-dialog";
// override-block:imports END

// override-block:helpers BEGIN
// RDM-R08 — labels mirror the runtime enums (7 statuses, 4 frequencies).
const STATUS_LABEL: Record<RecurringDonationStatus, string> = {
  pending: "Pending",
  active: "Active",
  paused: "Paused",
  past_due: "Past due",
  canceled: "Canceled",
  suspended: "Suspended",
  needs_reconciliation: "Needs reconciliation",
};
const FREQUENCY_LABEL: Record<RecurringFrequency, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

function donorLabel(row: RecurringDonation): string {
  if (row.donor_name) return row.donor_name;
  if (row.donor_email) return row.donor_email;
  // RDM-R08 — phone-only recurring donors (no email) get a useful contact.
  if (row.donor_phone) return row.donor_phone;
  return row.donor_id ? row.donor_id.slice(0, 8) + "…" : "—";
}

// RDM-R08 — designation_id is now nullable (public recurring donations may
// have none). Guard the slice so a null designation renders "—" instead of
// throwing "Cannot read properties of null (reading 'slice')".
function designationLabel(row: RecurringDonation): string {
  if (row.designation_name) return row.designation_name;
  if (row.designation_id) return row.designation_id.slice(0, 8) + "…";
  return "—";
}
// override-block:helpers END

export const recurringDonationListConfig: ListPageConfig<RecurringDonation> = {
  title: "Recurring donations",
  subtitle: "Active subscriptions and the donors behind them — protect retention with anniversary touchpoints and recovery messaging.",
  queryKey: ["recurring-donations", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    recurringDonationsApi.listRecurringDonations({ page, pageSize, sortField, sortDirection, search }),
  defaultSort: { field: "next_charge_at", direction: "asc" },
  pageSize: 50,
  rowHref: "/recurring/{id}",
  search: { enabled: true, placeholder: "Search recurring by donor name or email…" },
  ambientRow: {
    stageField: "stage",
    entity: "recurring",
    activeStages: ["at_risk_churn", "past_due"],
  },
  emptyState: {
    title: "No recurring donations yet",
    subtitle: "Recurring donations appear here once donors start a monthly or weekly gift.",
  },
  headerActions: [
    // override-block:header-actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.recurring_list["edit"], "disable") },
    { id: "pause", label: "Pause", variant: "ghost", permissionGate: toGate(GATE_CATALOG.recurring_list["pause"], "disable") },
    // override-block:header-actions END
  ],
  bulkActions: [
    {
      id: "tag",
      label: "Tag selected",
      variant: "primary",
      permission: "alora-giving.tag.attach",
      permissionFallback: "hide",
      onAction:
        // override-block:bulk-action-tag BEGIN
        (ids) => openBulkTagDialog({ entityType: "recurring_donation", ids }),
        // override-block:bulk-action-tag END
    },
  ],
  columns: [
    // override-block:column-donor-id BEGIN
    { field: "donor_id", label: "Donor", type: "custom", sortable: true, render: (row) => donorLabel(row) },
    // override-block:column-donor-id END
    { field: "amount_minor_units", label: "Amount", type: "currency", currencyField: "currency", sortable: true },
    // override-block:column-frequency BEGIN
    { field: "frequency", label: "Frequency", type: "custom", sortable: true, render: (row) => FREQUENCY_LABEL[row.frequency] ?? row.frequency },
    // override-block:column-frequency END
    { field: "next_charge_at", label: "Next charge", type: "relative-date", sortable: true },
    // override-block:column-status BEGIN
    { field: "status", label: "Status", type: "custom", render: (row) => STATUS_LABEL[row.status] ?? row.status },
    // override-block:column-status END
    // override-block:column-designation-id BEGIN
    { field: "designation_id", label: "Designation", type: "custom", render: (row) => designationLabel(row) },
    // override-block:column-designation-id END
    { field: "stage", label: "Stage", type: "stage-chip", entity: "recurring" },
  ],
};
